import * as THREE from 'three';
import { floraTime, makeFloraMaterials, type FloraFade } from './material';

interface Entry {
  key: string;
  refs: number;
  pair: ReturnType<typeof makeFloraMaterials>;
}
interface Lease { entry: Entry; options: FloraFade; mature: boolean }
const entries = new Map<string, Entry>();
const leases = new WeakMap<THREE.Mesh, Lease>();

function acquire(options: FloraFade): Entry {
  const key = JSON.stringify([options.birth ?? -100, options.fadeFar ?? 0, options.fadeNear ?? 0,
    options.height ?? null, !!options.rigid, !!options.simpleWind]);
  let entry = entries.get(key);
  if (!entry) {
    entry = { key, refs: 0, pair: makeFloraMaterials(options) };
    entries.set(key, entry);
  }
  entry.refs++;
  return entry;
}
function release(entry: Entry): void {
  if (--entry.refs) return;
  entries.delete(entry.key);
  entry.pair.material.dispose(); entry.pair.depthMaterial.dispose();
}
function assign(mesh: THREE.Mesh, entry: Entry): void {
  mesh.material = entry.pair.material;
  mesh.customDepthMaterial = entry.pair.depthMaterial;
}

/** Spatial cells share their exact uniform configuration. Once their birth fade
 * finishes they join the mature pool too: birth no longer varies the image. */
export function makePooledFloraMesh(geometry: THREE.BufferGeometry, count: number, options: FloraFade): THREE.InstancedMesh {
  const entry = acquire(options);
  const mesh = new THREE.InstancedMesh(geometry, entry.pair.material, count);
  assign(mesh, entry);
  leases.set(mesh, { entry, options, mature: options.birth === undefined });
  mesh.addEventListener('dispose', () => disposeFloraMeshMaterials(mesh));
  return mesh;
}

export function matureFloraMaterials(mesh: THREE.Mesh): void {
  const lease = leases.get(mesh);
  if (!lease || lease.mature || floraTime() - (lease.options.birth ?? -100) < .8) return;
  const old = lease.entry;
  lease.entry = acquire({ ...lease.options, birth: undefined });
  lease.mature = true;
  assign(mesh, lease.entry);
  release(old);
}

/** Used by scatter teardown for both pooled flora and older unique materials.
 * Mesh disposal also releases a lease, covering aborted builds. */
export function disposeFloraMeshMaterials(mesh: THREE.Mesh): void {
  const lease = leases.get(mesh);
  if (lease) {
    leases.delete(mesh); release(lease.entry);
  } else if (!released.has(mesh)) {
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) material.dispose();
    mesh.customDepthMaterial?.dispose();
  }
  released.add(mesh);
}
// Remember released leases without retaining the mesh, so a later dispose event
// cannot dispose a shared material for the second time.
const released = new WeakSet<THREE.Mesh>();
